import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/app/providers/AuthProvider";
import type { Session } from "@/features/auth/api/session.api";
import { sessionKeys } from "@/features/auth/api/session.queries";
import { isRestrictedSupportSession } from "@/lib/auth/support-session";
import { keys } from "@/lib/api/query-keys";
import { realtime } from "./realtime-client";
import { useRealtimeEvent } from "./useRealtime";
import type { NotificationCreatedPayload, NotificationSettledPayload } from "./realtime-events";

/**
 * Opens the stream while somebody is signed in, and closes it when they are not.
 *
 * A COMPONENT RATHER THAN A PROVIDER, and it renders nothing: there is no realtime context to
 * read — features subscribe to the client directly (`useRealtimeEvent`). What needs a place in
 * the tree is the LIFECYCLE, and the only fact it depends on is the session's status.
 *
 * Mounted inside the authenticated shell, so a stream is never opened on the sign-in page,
 * where it would 401 eight times on a backoff before giving up.
 *
 * It also owns the one subscription that belongs to no feature: a new notification refreshes
 * the bell. Chat, the converter and the rest subscribe from their own screens, because a
 * refetch is only worth making when the screen that shows it is on.
 */
export function RealtimeGate() {
  const { status, session, permissions } = useAuth();
  const queryClient = useQueryClient();
  /**
   * Destek oturumunda («Kullanıcı değiştir») akış açılmaz: sunucu onu 403'le reddediyor — yöneticiye
   * başkasının bildirimleri akmaz. Açmaya çalışmak yalnız boşa yeniden denemek ve kabukta yanlış bir
   * «Canlı bağlantı yok» uyarısı demekti (20 Ekim 2026). `standby` uyarıyı da susturur.
   *
   * Geliştirme kipinde (`supportSessionUnrestricted`, 30 Ekim 2026) akış AÇILIYOR: sunucu oturumu
   * kişinin kendisi sayıyor ve bağlantıyı kabul ediyor; sohbeti ve bildirimleri o kişinin gördüğü gibi
   * sınamak canlı akış istiyor. Bedeli bilinçli: kişi o sürede «çevrimiçi» görünür.
   */
  const support = isRestrictedSupportSession(session);
  /**
   * Akışın kapısı `notifications.notification.read` (sunucuda `notifications.stream.controller`).
   * Taşımacının şoförü yalnız «Şoför» rolüyle giriyor ve o anahtarı taşımıyor (1 Kasım 2026): açmaya
   * çalışmak 403'le geri çekilip yeniden denemek ve yanlış bir «Canlı bağlantı yok» uyarısı demekti.
   * Destek oturumu gibi `standby`.
   */
  const streamAllowed = permissions.can("notifications.notification.read");

  /** Whether the shell is still on screen when an awaited reopen finally resolves. */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (status !== "authenticated") {
      realtime.stop();
      return;
    }
    if (support || !streamAllowed) {
      realtime.standby();
      return;
    }
    realtime.start();
    return () => realtime.stop();
  }, [status, support, streamAllowed]);

  useRealtimeEvent<NotificationCreatedPayload>("notification.created", () => {
    void queryClient.invalidateQueries({ queryKey: keys.notifications.all() });
  });

  /**
   * 1 Kasım 2026: istediği iş halledilen bildirim HER alıcıda kapanıyor — başka bir onaycı onayladıysa da.
   * Zil sayacı ve açık kutu yeniden okunur; kapanan satır sunucunun listesinde artık yok.
   */
  useRealtimeEvent<NotificationSettledPayload>("notification.settled", () => {
    void queryClient.invalidateQueries({ queryKey: keys.notifications.all() });
  });

  /**
   * «ÇOKÇA DİNAMİK» (kisi-bilgi 19 Ekim 2026): İK bu kişinin adını, iletişimini ya da giriş
   * e-postasını değiştirdi. Oturum sorgusu bilerek hiç bayatlamıyor (`staleTime: Infinity`), yani
   * Panelim'deki «Merhaba …» ve formların «adına» satırı sayfa yenilenene kadar eski adı yazıyordu.
   * Çerçeve yalnız ipucu: oturum, Profil ve «Kişisel bilgilerim» yeniden okunuyor.
   */
  useRealtimeEvent("person.changed", () => {
    void queryClient.invalidateQueries({ queryKey: keys.session });
    void queryClient.invalidateQueries({ queryKey: keys.directory.me() });
    void queryClient.invalidateQueries({ queryKey: keys.hr.myDetails() });
  });

  /**
   * THE SERVER COULD NOT PROVE WHAT WE MISSED (§13 `resync`): an event id it no longer keeps, or a
   * backlog too long to replay. Everything ON SCREEN is re-read — and only what is on screen:
   * `invalidateQueries()` with no filter marks every entry stale but refetches the ACTIVE ones, so
   * a rail nobody is looking at costs nothing until it is opened.
   *
   * The opposite case is the point of the resume: after a `ready` that replayed cleanly, nothing
   * is invalidated here at all. Before 18 September every reconnect refetched the counters and the
   * open thread whether or not anything had happened.
   *
   * A FIRST CONNECT IS NOT A GAP (`fresh`, 19 September 2026, review finding 5). The server marks
   * the opening of a stream that carried no `since` — a client with no position missed nothing by
   * definition. Treating it as a gap cost a refetch of everything on EVERY page load, and worse
   * than a refetch: this component mounts in the same render as the shell's own queries, the frame
   * arrives one round trip later, and `invalidateQueries` cancels in-flight refetches by default —
   * so the opening queries were cancelled and re-issued, delaying the first paint. The only way
   * back to `lastEventId === null` is a sign-out or a customer switch, and the switch already
   * clears the cache outright.
   */
  useRealtimeEvent<{ fresh?: boolean }>("resync", (frame) => {
    if (frame.data.fresh === true) return;
    void queryClient.invalidateQueries();
  });

  /**
   * The session moved to another customer (the picker, or an administrator). Every cached answer
   * belongs to the customer it was read in, so the cache is dropped rather than invalidated: a
   * stale list re-rendered for two seconds would be the OTHER customer's rows on screen.
   */
  useRealtimeEvent("session.tenant_changed", () => {
    queryClient.clear();
    void queryClient.invalidateQueries({ queryKey: keys.session });
  });

  /**
   * The session ended elsewhere — an administrator revoked it, the person signed out in
   * another tab, or it expired. The server has already closed the socket; re-reading the
   * session is what moves this tab to the sign-in screen instead of leaving it on a dashboard
   * whose every request now 401s.
   */
  useRealtimeEvent("session.revoked", () => {
    void queryClient.invalidateQueries({ queryKey: keys.session });
  });

  /**
   * The session's own expiry passed, so the server closed the stream and the client stopped
   * retrying into a dead session. Re-reading the session decides what happens next: a browser
   * whose refresh cookie is still good gets a new session and stays signed in, and then needs
   * its stream BACK — without this second `start()` it would sit silently for the rest of the
   * shift, because `status` never left "authenticated" and the effect above never re-runs.
   *
   * THE REOPEN IS CONDITIONAL, and the condition is the point. This runs after an await, so by
   * the time it resolves the answer may be that there is no session at all. Reopening anyway
   * would hand a signed-out tab exactly what this component exists to prevent: a stream on the
   * sign-in page, 401-ing through all eight backoff attempts, with no effect left to re-run and
   * stop it — the lifecycle effect above has already called `stop()` by then. So the cache is
   * asked what the re-read actually produced. A refetch that failed leaves the query in `error`
   * while still holding the PREVIOUS session as its data, which is why the status is checked
   * and not just the value.
   */
  useRealtimeEvent("expired", () => {
    void queryClient.invalidateQueries({ queryKey: keys.session }).then(() => {
      if (!mounted.current) return;
      const state = queryClient.getQueryState(sessionKeys.current());
      if (state?.status !== "success" || state.data == null) return;
      // Re-read into a restricted support session: still no stream (see `support` above).
      if (isRestrictedSupportSession(state.data as Session)) return;
      realtime.start();
    });
  });

  return null;
}
